// FeeCalculator.jsx
import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Typography,
  Grid,
  IconButton,
  Collapse,
  useMediaQuery,
  Button,
  Menu,
  Tooltip,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import config from "../../../config/config.json";
import { useAuth } from "../../../context/AuthContext";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import FeeSummaryPdf from "./FeeSummaryPdf"; // Import the separate PDF component
import { AccountCircle } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

export default function FeeCalculator() {
  const [selectedCurrency, setSelectedCurrency] = useState("CAD");
  const [feeData, setFeeData] = useState(null);
  const [durationOptions, setDurationOptions] = useState([]);
  const [currencyRates, setCurrencyRates] = useState([]);
  const [formState, setFormState] = useState({});
  const [totalFee, setTotalFee] = useState(0);
  const isMobile = useMediaQuery("(max-width:768px)");
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const { token, logout } = useAuth();
  const feeSummaryRef = useRef();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState(null);

  const pdfComponentRef = useRef();

  const currencyOptions = [
    { value: "CAD", label: "CAD - Canadian Dollar", symbol: "C$" },
    { value: "AED", label: "AED - United Arab Emirates Dirham", symbol: "د.إ" },
    { value: "AFN", label: "AFN - Afghan Afghani", symbol: "؋" },
    { value: "ALL", label: "ALL - Albanian Lek", symbol: "L" },
    { value: "AMD", label: "AMD - Armenian Dram", symbol: "֏" },
    { value: "ANG", label: "ANG - Netherlands Antillean Guilder", symbol: "ƒ" },
    { value: "AOA", label: "AOA - Angolan Kwanza", symbol: "Kz" },
    { value: "ARS", label: "ARS - Argentine Peso", symbol: "$" },
    { value: "AUD", label: "AUD - Australian Dollar", symbol: "A$" },
    { value: "AWG", label: "AWG - Aruban Florin", symbol: "ƒ" },
    { value: "AZN", label: "AZN - Azerbaijani Manat", symbol: "₼" },
    {
      value: "BAM",
      label: "BAM - Bosnia-Herzegovina Convertible Mark",
      symbol: "KM",
    },
    { value: "BBD", label: "BBD - Barbadian Dollar", symbol: "Bds$" },
    { value: "BDT", label: "BDT - Bangladeshi Taka", symbol: "৳" },
    { value: "BGN", label: "BGN - Bulgarian Lev", symbol: "лв" },
    { value: "BHD", label: "BHD - Bahraini Dinar", symbol: ".د.ب" },
    { value: "BIF", label: "BIF - Burundian Franc", symbol: "FBu" },
    { value: "BMD", label: "BMD - Bermudian Dollar", symbol: "$" },
    { value: "BND", label: "BND - Brunei Dollar", symbol: "B$" },
    { value: "BOB", label: "BOB - Bolivian Boliviano", symbol: "Bs." },
    { value: "BRL", label: "BRL - Brazilian Real", symbol: "R$" },
    { value: "BSD", label: "BSD - Bahamian Dollar", symbol: "B$" },
    { value: "BTN", label: "BTN - Bhutanese Ngultrum", symbol: "Nu." },
    { value: "BWP", label: "BWP - Botswana Pula", symbol: "P" },
    { value: "BYN", label: "BYN - Belarusian Ruble", symbol: "Br" },
    { value: "BZD", label: "BZD - Belize Dollar", symbol: "BZ$" },
    { value: "CDF", label: "CDF - Congolese Franc", symbol: "FC" },
    { value: "CHF", label: "CHF - Swiss Franc", symbol: "Fr." },
    { value: "CLP", label: "CLP - Chilean Peso", symbol: "$" },
    { value: "CNY", label: "CNY - Chinese Yuan", symbol: "¥" },
    { value: "COP", label: "COP - Colombian Peso", symbol: "$" },
    { value: "CRC", label: "CRC - Costa Rican Colón", symbol: "₡" },
    { value: "CUP", label: "CUP - Cuban Peso", symbol: "$" },
    { value: "CVE", label: "CVE - Cape Verdean Escudo", symbol: "$" },
    { value: "CZK", label: "CZK - Czech Koruna", symbol: "Kč" },
    { value: "DJF", label: "DJF - Djiboutian Franc", symbol: "Fdj" },
    { value: "DKK", label: "DKK - Danish Krone", symbol: "kr" },
    { value: "DOP", label: "DOP - Dominican Peso", symbol: "RD$" },
    { value: "DZD", label: "DZD - Algerian Dinar", symbol: "دج" },
    { value: "EGP", label: "EGP - Egyptian Pound", symbol: "£" },
    { value: "ERN", label: "ERN - Eritrean Nakfa", symbol: "Nfk" },
    { value: "ETB", label: "ETB - Ethiopian Birr", symbol: "Br" },
    { value: "EUR", label: "EUR - Euro", symbol: "€" },
    { value: "FJD", label: "FJD - Fijian Dollar", symbol: "FJ$" },
    { value: "FKP", label: "FKP - Falkland Islands Pound", symbol: "£" },
    { value: "FOK", label: "FOK - Faroese Króna", symbol: "kr" },
    { value: "GBP", label: "GBP - British Pound Sterling", symbol: "£" },
    { value: "GEL", label: "GEL - Georgian Lari", symbol: "₾" },
    { value: "GGP", label: "GGP - Guernsey Pound", symbol: "£" },
    { value: "GHS", label: "GHS - Ghanaian Cedi", symbol: "₵" },
    { value: "GIP", label: "GIP - Gibraltar Pound", symbol: "£" },
    { value: "GMD", label: "GMD - Gambian Dalasi", symbol: "D" },
    { value: "GNF", label: "GNF - Guinean Franc", symbol: "FG" },
    { value: "GTQ", label: "GTQ - Guatemalan Quetzal", symbol: "Q" },
    { value: "GYD", label: "GYD - Guyanese Dollar", symbol: "G$" },
    { value: "HKD", label: "HKD - Hong Kong Dollar", symbol: "HK$" },
    { value: "HNL", label: "HNL - Honduran Lempira", symbol: "L" },
    { value: "HRK", label: "HRK - Croatian Kuna", symbol: "kn" },
    { value: "HTG", label: "HTG - Haitian Gourde", symbol: "G" },
    { value: "HUF", label: "HUF - Hungarian Forint", symbol: "Ft" },
    { value: "IDR", label: "IDR - Indonesian Rupiah", symbol: "Rp" },
    { value: "ILS", label: "ILS - Israeli New Shekel", symbol: "₪" },
    { value: "IMP", label: "IMP - Isle of Man Pound", symbol: "£" },
    { value: "INR", label: "INR - Indian Rupee", symbol: "₹" },
    { value: "IQD", label: "IQD - Iraqi Dinar", symbol: "ع.د" },
    { value: "IRR", label: "IRR - Iranian Rial", symbol: "﷼" },
    { value: "ISK", label: "ISK - Icelandic Króna", symbol: "kr" },
    { value: "JEP", label: "JEP - Jersey Pound", symbol: "£" },
    { value: "JMD", label: "JMD - Jamaican Dollar", symbol: "J$" },
    { value: "JOD", label: "JOD - Jordanian Dinar", symbol: "JD" },
    { value: "JPY", label: "JPY - Japanese Yen", symbol: "¥" },
    { value: "KES", label: "KES - Kenyan Shilling", symbol: "KSh" },
    { value: "KGS", label: "KGS - Kyrgyzstani Som", symbol: "лв" },
    { value: "KHR", label: "KHR - Cambodian Riel", symbol: "៛" },
    { value: "KID", label: "KID - Kiribati Dollar", symbol: "A$" },
    { value: "KMF", label: "KMF - Comorian Franc", symbol: "CF" },
    { value: "KRW", label: "KRW - South Korean Won", symbol: "₩" },
    { value: "KWD", label: "KWD - Kuwaiti Dinar", symbol: "د.ك" },
    { value: "KYD", label: "KYD - Cayman Islands Dollar", symbol: "CI$" },
    { value: "KZT", label: "KZT - Kazakhstani Tenge", symbol: "₸" },
    { value: "LAK", label: "LAK - Lao Kip", symbol: "₭" },
    { value: "LBP", label: "LBP - Lebanese Pound", symbol: "ل.ل" },
    { value: "LKR", label: "LKR - Sri Lankan Rupee", symbol: "Rs" },
    { value: "LRD", label: "LRD - Liberian Dollar", symbol: "L$" },
    { value: "LSL", label: "LSL - Lesotho Loti", symbol: "M" },
    { value: "LYD", label: "LYD - Libyan Dinar", symbol: "ل.د" },
    { value: "MAD", label: "MAD - Moroccan Dirham", symbol: "د.م." },
    { value: "MDL", label: "MDL - Moldovan Leu", symbol: "L" },
    { value: "MGA", label: "MGA - Malagasy Ariary", symbol: "Ar" },
    { value: "MKD", label: "MKD - Macedonian Denar", symbol: "ден" },
    { value: "MMK", label: "MMK - Myanmar Kyat", symbol: "K" },
    { value: "MNT", label: "MNT - Mongolian Tögrög", symbol: "₮" },
    { value: "MOP", label: "MOP - Macanese Pataca", symbol: "P" },
    { value: "MRU", label: "MRU - Mauritanian Ouguiya", symbol: "UM" },
    { value: "MUR", label: "MUR - Mauritian Rupee", symbol: "₨" },
    { value: "MVR", label: "MVR - Maldivian Rufiyaa", symbol: "Rf" },
    { value: "MWK", label: "MWK - Malawian Kwacha", symbol: "MK" },
    { value: "MXN", label: "MXN - Mexican Peso", symbol: "$" },
    { value: "MYR", label: "MYR - Malaysian Ringgit", symbol: "RM" },
    { value: "MZN", label: "MZN - Mozambican Metical", symbol: "MT" },
    { value: "NAD", label: "NAD - Namibian Dollar", symbol: "N$" },
    { value: "NGN", label: "NGN - Nigerian Naira", symbol: "₦" },
    { value: "NIO", label: "NIO - Nicaraguan Córdoba", symbol: "C$" },
    { value: "NOK", label: "NOK - Norwegian Krone", symbol: "kr" },
    { value: "NPR", label: "NPR - Nepalese Rupee", symbol: "₨" },
    { value: "NZD", label: "NZD - New Zealand Dollar", symbol: "NZ$" },
    { value: "OMR", label: "OMR - Omani Rial", symbol: "ر.ع." },
    { value: "PAB", label: "PAB - Panamanian Balboa", symbol: "B/." },
    { value: "PEN", label: "PEN - Peruvian Sol", symbol: "S/." },
    { value: "PGK", label: "PGK - Papua New Guinean Kina", symbol: "K" },
    { value: "PHP", label: "PHP - Philippine Peso", symbol: "₱" },
    { value: "PKR", label: "PKR - Pakistani Rupee", symbol: "₨" },
    { value: "PLN", label: "PLN - Polish Złoty", symbol: "zł" },
    { value: "PYG", label: "PYG - Paraguayan Guaraní", symbol: "₲" },
    { value: "QAR", label: "QAR - Qatari Riyal", symbol: "ر.ق" },
    { value: "RON", label: "RON - Romanian Leu", symbol: "lei" },
    { value: "RSD", label: "RSD - Serbian Dinar", symbol: "дин" },
    { value: "RUB", label: "RUB - Russian Ruble", symbol: "₽" },
    { value: "RWF", label: "RWF - Rwandan Franc", symbol: "FRw" },
    { value: "SAR", label: "SAR - Saudi Riyal", symbol: "ر.س" },
    { value: "SBD", label: "SBD - Solomon Islands Dollar", symbol: "SI$" },
    { value: "SCR", label: "SCR - Seychellois Rupee", symbol: "₨" },
    { value: "SDG", label: "SDG - Sudanese Pound", symbol: "ج.س." },
    { value: "SEK", label: "SEK - Swedish Krona", symbol: "kr" },
    { value: "SGD", label: "SGD - Singapore Dollar", symbol: "S$" },
    { value: "SHP", label: "SHP - Saint Helena Pound", symbol: "£" },
    { value: "SLE", label: "SLE - Sierra Leonean Leone", symbol: "Le" },
    { value: "SLL", label: "SLL - Sierra Leonean Leone (old)", symbol: "Le" },
    { value: "SOS", label: "SOS - Somali Shilling", symbol: "Sh.So." },
    { value: "SRD", label: "SRD - Surinamese Dollar", symbol: "$" },
    { value: "SSP", label: "SSP - South Sudanese Pound", symbol: "£" },
    { value: "STN", label: "STN - São Tomé and Príncipe Dobra", symbol: "Db" },
    { value: "SYP", label: "SYP - Syrian Pound", symbol: "£" },
    { value: "SZL", label: "SZL - Swazi Lilangeni", symbol: "E" },
    { value: "THB", label: "THB - Thai Baht", symbol: "฿" },
    { value: "TJS", label: "TJS - Tajikistani Somoni", symbol: "ЅM" },
    { value: "TMT", label: "TMT - Turkmenistan Manat", symbol: "m" },
    { value: "TND", label: "TND - Tunisian Dinar", symbol: "د.ت" },
    { value: "TOP", label: "TOP - Tongan Paʻanga", symbol: "T$" },
    { value: "TRY", label: "TRY - Turkish Lira", symbol: "₺" },
    { value: "TTD", label: "TTD - Trinidad and Tobago Dollar", symbol: "TT$" },
    { value: "TVD", label: "TVD - Tuvaluan Dollar", symbol: "$" },
    { value: "TWD", label: "TWD - New Taiwan Dollar", symbol: "NT$" },
    { value: "TZS", label: "TZS - Tanzanian Shilling", symbol: "TSh" },
    { value: "UAH", label: "UAH - Ukrainian Hryvnia", symbol: "₴" },
    { value: "UGX", label: "UGX - Ugandan Shilling", symbol: "USh" },
    { value: "USD", label: "USD - United States Dollar", symbol: "$" },
    { value: "UYU", label: "UYU - Uruguayan Peso", symbol: "$U" },
    { value: "UZS", label: "UZS - Uzbekistani Soʻm", symbol: "so'm" },
    {
      value: "VES",
      label: "VES - Venezuelan Bolívar Soberano",
      symbol: "Bs.S",
    },
    { value: "VND", label: "VND - Vietnamese Đồng", symbol: "₫" },
    { value: "VUV", label: "VUV - Vanuatu Vatu", symbol: "VT" },
    { value: "WST", label: "WST - Samoan Tālā", symbol: "WS$" },
    { value: "XAF", label: "XAF - Central African CFA Franc", symbol: "FCFA" },
    { value: "XCD", label: "XCD - East Caribbean Dollar", symbol: "EC$" },
    { value: "XCG", label: "XCG - Unrecognized Currency", symbol: "XCG" },
    { value: "XDR", label: "XDR - IMF Special Drawing Rights", symbol: "SDR" },
    { value: "XOF", label: "XOF - West African CFA franc", symbol: "CFA" },
    { value: "XPF", label: "XPF - CFP Franc", symbol: "₣" },
    { value: "YER", label: "YER - Yemeni Rial", symbol: "﷼" },
    { value: "ZAR", label: "ZAR - South African Rand", symbol: "R" },
    { value: "ZMW", label: "ZMW - Zambian Kwacha", symbol: "ZK" },
    { value: "ZWL", label: "ZWL - Zimbabwean Dollar", symbol: "Z$" },
  ];

  const getCurrencySymbol = () => {
    const currency = currencyOptions.find((c) => c.value === selectedCurrency);
    return currency?.symbol || "$";
  };

  useEffect(() => {
    const fetchFees = async () => {
      try {
        // fetch user id from url
        const userId = window.location.pathname.split("/").pop();
        console.log("user id", userId);

        const res = await axios.get(`${config.base_url}/api/fees/${userId}`, {
          headers: {
            Authorization: `${token}`, // Or `Bearer ${token}` if your API expects Bearer tokens
          },
        });

        const data = res.data;
        console.log(data, "data");

        setFeeData(data);

        setDurationOptions(
          data.durations
            .sort((a, b) => a.monthsEquiv - b.monthsEquiv)
            .map((d) => ({
              value: d.id,
              label: d.name,
              months: d.monthsEquiv,
            }))
        );

        setCurrencyRates(data.currencyRates);
      } catch (err) {
        if (err.response) {
          const status = err.response.status;

          if (status === 401) {
            console.error("Unauthorized - redirecting to login.");
            logout();
            return;
          }

          if (status === 404) {
            console.error("404 Not Found - No fee data available.");
            setNotFound(true);
            return;
          }
        }

        console.error("Failed to load fee data", err);
      }
    };

    fetchFees();
  }, []);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleMenuClose();
    logout();
  };

  const handleDownloadPdf = async () => {
    const element = pdfComponentRef.current;

    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
    });

    const imgData = canvas.toDataURL("image/png");
    const pdf = new jsPDF("p", "mm", "a4");

    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const imgHeight = (canvas.height * pdfWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = 0;

    while (heightLeft > 0) {
      pdf.addImage(imgData, "PNG", 0, position, pdfWidth, imgHeight);
      heightLeft -= pdfHeight;
      if (heightLeft > 0) {
        pdf.addPage();
        position -= pdfHeight;
      }
    }

    pdf.save("fee-summary.pdf");
  };

  const getFeeAmount = (category, durationId, subCategory = null) => {
    const fees = feeData?.feesByCategory?.[category]?.fees || [];
    if (subCategory) {
      return (
        fees.find(
          (f) =>
            f.durationId === durationId && f.subCategoryName === subCategory
        )?.amount || 0
      );
    }
    return fees.find((f) => f.durationId === durationId)?.amount || 0;
  };

  const convertCurrency = (amount) => {
    if (selectedCurrency === "CAD") return amount;
    const rate = currencyRates.find(
      (r) => r.targetCurrency === selectedCurrency
    );
    return rate ? amount * rate.rate : amount;
  };

  const calculateTotal = () => {
    if (!feeData) return 0;
    let total = 0;

    for (const category of feeData.feeCategories) {
      const catName = category.name;
      const value = formState[catName];
      if (!value?.enabled) continue;

      if (category.hasSubCategory) {
        for (const sub of feeData.subCategoriesByParent[catName] || []) {
          const subVal = value?.subDurations?.[sub.name];
          if (subVal) {
            total += getFeeAmount(catName, subVal, sub.name);
          }
        }
      } else {
        if (value.durationId) {
          total += getFeeAmount(catName, value.durationId);
        }
      }
    }

    return total;
  };

  useEffect(() => {
    setTotalFee(calculateTotal());
  }, [formState, selectedCurrency]);

  const handleDurationChange = (category, value, subCategory = null) => {
    setFormState((prev) => {
      const updated = { ...prev };

      if (!updated[category]) {
        updated[category] = { enabled: true };
      }

      if (subCategory) {
        if (!updated[category].subDurations) {
          updated[category].subDurations = {};
        }

        if (value === "") {
          delete updated[category].subDurations[subCategory];

          // Optional: clean up empty subDurations object
          if (Object.keys(updated[category].subDurations).length === 0) {
            delete updated[category].subDurations;
          }
        } else {
          updated[category].subDurations[subCategory] = value;
        }
      } else {
        if (value === "") {
          delete updated[category].durationId;
        } else {
          updated[category].durationId = value;
        }
      }

      return updated;
    });
  };

  const toggleCategory = (category) => {
    setFormState((prev) => ({
      ...prev,
      [category]: {
        ...prev[category],
        enabled: !prev[category]?.enabled,
      },
    }));
  };

  if (notFound) {
    return (
      <Box p={4} textAlign="center">
        <Typography variant="h5" color="error">
          404 - Invalid url.
        </Typography>
      </Box>
    );
  }

  return (
    <Box className="fee-calculator-container">
      {/* <Typography variant="h4" align="center" mb={2}>
        <img
          src="/assets/images/osa-logo.svg"
          alt="Fee Calculator"
          className="fee-header-image"
        />
        Fee Calculator
        
      </Typography> */}
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        mb={2}
        position="relative"
      >
        <img
          src="/assets/images/osa-logo.svg"
          alt="Fee Calculator"
          className="fee-header-image"
          sx={{
            height: "50px",
            marginRight: "10px",
            marginLeft: { xs: "-57px", sm: "0" }, // responsive only works in sx
          }}
        />
        <Typography variant="h4" component="div">
          Fee Calculator
        </Typography>

        {/* Dropdown Icon on Right of Logo */}
        <Box position="absolute" right={0}>
          <IconButton onClick={handleMenuOpen}>
            <AccountCircle sx={{ color: "var(--secondary)" }} />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
          >
            <MenuItem onClick={handleLogout}>Logout</MenuItem>
          </Menu>
        </Box>
      </Box>

      <Box className="fee-calculator-columns">
        {/* Left Side: Fee Categories */}
        <Box className="fee-categories-scroll">
          {feeData && (
            <>
              {/* === Fixed Fees Heading === */}
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  mt: 2,
                  ml: 2,
                  color: "var(--secondary)",
                  fontWeight: "bold",
                }}
              >
                Fixed Fees
              </Typography>

              {/* Fixed Categories in Desired Order First */}
              {["Tuition", "Uniform", "Homestay"]
                .map((name) =>
                  feeData.feeCategories.find(
                    (cat) => cat.name === name && cat.type === "fixed"
                  )
                )
                .filter(Boolean)
                .map((category) => (
                  <Card
                    className="fee-card"
                    variant="outlined"
                    key={category.name}
                  >
                    <CardHeader
                      className="card-header"
                      title={
                        <Typography variant="h6" className="fee-card-header">
                          {category.name}
                        </Typography>
                      }
                    />
                    <CardContent className="card-body">
                      {category.hasSubCategory ? (
                        (
                          feeData.subCategoriesByParent[category.name] || []
                        ).map((sub) => (
                          <Box key={sub.name} mb={2}>
                            <FormControl fullWidth>
                              <InputLabel>{sub.name} Duration</InputLabel>
                              <Select
                                value={
                                  formState[category.name]?.subDurations?.[
                                    sub.name
                                  ] || ""
                                }
                                onChange={(e) =>
                                  handleDurationChange(
                                    category.name,
                                    e.target.value,
                                    sub.name
                                  )
                                }
                                label={`${sub.name} Duration`}
                              >
                                <MenuItem value="">None</MenuItem>
                                {durationOptions.map((opt) => (
                                  <MenuItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Box>
                        ))
                      ) : (
                        <FormControl fullWidth>
                          <InputLabel>Duration</InputLabel>
                          <Select
                            value={formState[category.name]?.durationId || ""}
                            onChange={(e) =>
                              handleDurationChange(
                                category.name,
                                e.target.value
                              )
                            }
                            label="Duration"
                          >
                            <MenuItem value="">None</MenuItem>
                            {durationOptions.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    </CardContent>
                  </Card>
                ))}

              {/* Remaining Fixed Categories (excluding those already shown) */}
              {feeData.feeCategories
                .filter(
                  (cat) =>
                    cat.type === "fixed" &&
                    !["Tuition", "Uniform", "Homestay"].includes(cat.name)
                )
                .map((category) => (
                  <Card
                    className="fee-card"
                    variant="outlined"
                    key={category.name}
                  >
                    <CardHeader
                      className="card-header"
                      title={
                        <Typography variant="h6" className="fee-card-header">
                          {category.name}
                        </Typography>
                      }
                    />
                    <CardContent className="card-body">
                      {category.hasSubCategory ? (
                        (
                          feeData.subCategoriesByParent[category.name] || []
                        ).map((sub) => (
                          <Box key={sub.name} mb={2}>
                            <FormControl fullWidth>
                              <InputLabel>{sub.name} Duration</InputLabel>
                              <Select
                                value={
                                  formState[category.name]?.subDurations?.[
                                    sub.name
                                  ] || ""
                                }
                                onChange={(e) =>
                                  handleDurationChange(
                                    category.name,
                                    e.target.value,
                                    sub.name
                                  )
                                }
                                label={`${sub.name} Duration`}
                              >
                                <MenuItem value="">None</MenuItem>
                                {durationOptions.map((opt) => (
                                  <MenuItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Box>
                        ))
                      ) : (
                        <FormControl fullWidth>
                          <InputLabel>Duration</InputLabel>
                          <Select
                            value={formState[category.name]?.durationId || ""}
                            onChange={(e) =>
                              handleDurationChange(
                                category.name,
                                e.target.value
                              )
                            }
                            label="Duration"
                          >
                            <MenuItem value="">None</MenuItem>
                            {durationOptions.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    </CardContent>
                  </Card>
                ))}

              {/* === Add-On Heading === */}
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  mt: 4,
                  ml: 2,
                  color: "var(--secondary)",
                  fontWeight: "bold",
                }}
              >
                Add-On Fees
              </Typography>

              {/* Add-On Categories */}
              {feeData.feeCategories
                .filter((cat) => cat.type === "add-on")
                .map((category) => (
                  <Card
                    className="fee-card"
                    variant="outlined"
                    key={category.name}
                  >
                    <CardHeader
                      className="card-header"
                      title={
                        <Box display="flex" alignItems="center">
                          <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            textAlign="center"
                            mr={1}
                          >
                            <Checkbox
                              checked={
                                formState[category.name]?.enabled || false
                              }
                              onChange={() => toggleCategory(category.name)}
                            />
                          </Box>
                          <Typography variant="h6" className="fee-card-header">
                            {category.name}
                          </Typography>
                        </Box>
                      }
                    />
                    <CardContent className="card-body">
                      {category.hasSubCategory ? (
                        (
                          feeData.subCategoriesByParent[category.name] || []
                        ).map((sub) => (
                          <Box key={sub.name} mb={2}>
                            <FormControl fullWidth>
                              <InputLabel>{sub.name} Duration</InputLabel>
                              <Select
                                value={
                                  formState[category.name]?.subDurations?.[
                                    sub.name
                                  ] || ""
                                }
                                onChange={(e) =>
                                  handleDurationChange(
                                    category.name,
                                    e.target.value,
                                    sub.name
                                  )
                                }
                                label={`${sub.name} Duration`}
                              >
                                <MenuItem value="">None</MenuItem>
                                {durationOptions.map((opt) => (
                                  <MenuItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </MenuItem>
                                ))}
                              </Select>
                            </FormControl>
                          </Box>
                        ))
                      ) : (
                        <FormControl fullWidth>
                          <InputLabel>Duration</InputLabel>
                          <Select
                            value={formState[category.name]?.durationId || ""}
                            onChange={(e) =>
                              handleDurationChange(
                                category.name,
                                e.target.value
                              )
                            }
                            label="Duration"
                          >
                            <MenuItem value="">None</MenuItem>
                            {durationOptions.map((opt) => (
                              <MenuItem key={opt.value} value={opt.value}>
                                {opt.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}
                    </CardContent>
                  </Card>
                ))}
            </>
          )}
        </Box>

        {/* Right Side: Fee Summary */}
        <Box ref={feeSummaryRef} className="fee-summary-scroll">
          <Card className="fee-card">
            <CardContent>
              <Grid
                container
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Grid item>
                  <FormControl sx={{ minWidth: 160 }}>
                    <InputLabel id="currency-select-label">Currency</InputLabel>
                    <Select
                      label="Currency"
                      labelId="currency-select-label"
                      value={selectedCurrency}
                      onChange={(e) => setSelectedCurrency(e.target.value)}
                    >
                      {currencyOptions.map((c) => (
                        <MenuItem key={c.value} value={c.value}>
                          {c.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item>
                  <Box textAlign="right">
                    {/* Always show in CAD */}
                    <Typography
                      variant="h5"
                      fontWeight="bold"
                      className="fee-text"
                    >
                      CAD ${totalFee.toLocaleString()}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: "var(--primary)" }}
                    >
                      Base total in CAD
                    </Typography>

                    {/* If selectedCurrency is not CAD, show estimated conversion */}
                    {selectedCurrency !== "CAD" && (
                      <>
                        <Typography
                          variant="h6"
                          fontWeight="medium"
                          className="fee-text"
                          sx={{ mt: 1 }}
                        >
                          {getCurrencySymbol()}
                          {convertCurrency(totalFee).toLocaleString()}
                        </Typography>
                        <Typography variant="body2" sx={{ color: "gray" }}>
                          Estimated in {selectedCurrency}
                        </Typography>
                      </>
                    )}
                  </Box>
                </Grid>
              </Grid>

              {/* Toggle Button for Mobile */}
              {isMobile && (
                <IconButton
                  size="small"
                  onClick={() => setBreakdownOpen(!breakdownOpen)}
                  sx={{ mb: 1, display: "flex", alignItems: "center" }}
                >
                  {breakdownOpen ? (
                    <>
                      <ExpandLessIcon sx={{ mr: 0.5 }} />
                      <Typography variant="body2">
                        Collapse breakdown
                      </Typography>
                    </>
                  ) : (
                    <>
                      <ExpandMoreIcon sx={{ mr: 0.5 }} />
                      <Typography variant="body2">Show breakdown</Typography>
                    </>
                  )}
                </IconButton>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Collapsible Breakdown Section */}
              <Collapse in={!isMobile || breakdownOpen}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  mb={1}
                >
                  <Typography
                    variant="h6"
                    className="breakdown-text"
                    gutterBottom
                  >
                    Breakdown
                  </Typography>

                  <Tooltip
                    title="Download breakdown as PDF"
                    arrow
                    fontWeight="bold"
                  >
                    <IconButton
                      onClick={handleDownloadPdf}
                      color="var(--secondary)"
                    >
                      <DownloadIcon />
                    </IconButton>
                  </Tooltip>
                </Box>

                {feeData?.feeCategories
                  .slice()
                  .sort((a, b) => {
                    const priority = ["Tuition", "Homestay", "Uniform"];
                    const aIndex = priority.includes(a.name)
                      ? priority.indexOf(a.name)
                      : 99;
                    const bIndex = priority.includes(b.name)
                      ? priority.indexOf(b.name)
                      : 99;
                    return aIndex - bIndex;
                  })
                  .map((category) => {
                    const catState = formState[category.name];
                    if (!catState?.enabled && category.type === "add-on")
                      return null;

                    if (category.hasSubCategory) {
                      return (
                        feeData.subCategoriesByParent[category.name] || []
                      ).map((sub) => {
                        const durationId = catState?.subDurations?.[sub.name];
                        if (!durationId) return null;
                        const durationLabel =
                          durationOptions.find((d) => d.value === durationId)
                            ?.label || "";
                        const rawAmount = getFeeAmount(
                          category.name,
                          durationId,
                          sub.name
                        );
                        const convertedAmount =
                          convertCurrency(rawAmount).toFixed(2);

                        return (
                          <Box key={`${category.name}-${sub.name}`} mb={1}>
                            <Typography variant="body2">
                              <strong>
                                {category.name} → {sub.name}
                              </strong>
                              : {durationLabel} — {getCurrencySymbol()}
                              {convertedAmount}
                            </Typography>
                          </Box>
                        );
                      });
                    } else {
                      const durationId = catState?.durationId;
                      if (!durationId) return null;
                      const durationLabel =
                        durationOptions.find((d) => d.value === durationId)
                          ?.label || "";
                      const rawAmount = getFeeAmount(category.name, durationId);
                      const convertedAmount =
                        convertCurrency(rawAmount).toFixed(2);

                      return (
                        <Box key={category.name} mb={1}>
                          <Typography variant="body2">
                            <strong>{category.name}</strong>: {durationLabel} —{" "}
                            {getCurrencySymbol()}
                            {convertedAmount}
                          </Typography>
                        </Box>
                      );
                    }
                  })}

                <Box mb={2} mt={2}>
                  <Typography
                    variant="body2"
                    sx={{ color: "var(--secondary)", fontWeight: 600, mb: 0.5 }}
                  >
                    Important to note:
                  </Typography>
                  <ul
                    style={{
                      margin: 0,
                      paddingLeft: "1.25rem",
                      color: "black",
                    }}
                  >
                    <li>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        All fees are invoiced in CAD.
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        We do not invoice for Homestay fees, so all fees are an
                        estimate and you will need to confirm with Excel Abroad
                        Homestay for firm confirmation. <b>Email: </b>
                        excelabroad01@gmail.com
                      </Typography>
                    </li>
                  </ul>
                </Box>
              </Collapse>
            </CardContent>
          </Card>
        </Box>

        {/* PDF Generator Component (HIDDEN) */}
        <div style={{ position: "absolute", left: "-9999px", top: 0 }}>
          <FeeSummaryPdf
            ref={pdfComponentRef}
            totalFee={totalFee}
            selectedCurrency={selectedCurrency}
            convertCurrency={convertCurrency}
            getCurrencySymbol={getCurrencySymbol}
            feeData={feeData}
            formState={formState}
            durationOptions={durationOptions}
            getFeeAmount={getFeeAmount}
            currencyOptions={currencyOptions}
          />
        </div>
      </Box>
    </Box>
  );
}
